import * as React from 'react';

import { useQuery } from '@apollo/client';
import useMedia from 'use-media';

import {
  RoutineByProductIdQuery,
  RoutineByProductIdQueryVariables,
} from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import { MAX_MEDIUM_BREAKPOINT } from '@components/Universals/Routine';
import { Routine } from '@components/Universals/Routine/loadable';
import routineByProductId from '@components/Universals/Routine/queries/routineByProductId.graphql';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import { useProductDetailContext } from '../../ProductDetailContext';
import { RelatedProducts } from '../RelatedProducts';
import { Clear } from '../styled';
import { isDiorBrand } from '../utils';

interface IRoutineOrRelatedProducts {
  productCode: string;
  relatedProducts: string[];
}

export const RoutineOrRelatedProducts: React.FC<IRoutineOrRelatedProducts> =
  React.memo(({ relatedProducts, productCode }) => {
    const {
      product: { brand, catalogMasterId },
      currentVariant: { catalogId },
    } = useProductDetailContext();

    const isMobile = useMedia(MAX_MEDIUM_BREAKPOINT);

    const { reco_product_detail } = useFeatureFlags();

    const { data, loading, error } = useQuery<
      RoutineByProductIdQuery,
      RoutineByProductIdQueryVariables
    >(routineByProductId, {
      variables: {
        productId: Number(catalogId),
        masterProductId: Number(catalogMasterId),
      },
      ssr: false,
      errorPolicy: 'all',
    });

    const displayRoutine =
      !error &&
      data?.RoutineByProductId?.available &&
      !!data?.RoutineByProductId?.routines?.length;

    const displayRelatedProducts = relatedProducts?.length > 0;

    const getRelatedProductsComponent = React.useCallback(
      () => (
        <RelatedProducts
          currentProductCode={productCode}
          items={relatedProducts}
          shouldRenderRecommendedProducts={
            !!reco_product_detail && !isDiorBrand(brand?.id ?? '')
          }
        />
      ),
      [productCode, relatedProducts, reco_product_detail, brand?.id]
    );

    if (
      loading ||
      (!displayRoutine && !displayRelatedProducts && !reco_product_detail)
    ) {
      return null;
    }

    return displayRoutine && data?.RoutineByProductId?.routines ? (
      <ErrorBoundary fallback={getRelatedProductsComponent}>
        <Routine routine={data.RoutineByProductId.routines[0]!} />

        {isMobile && getRelatedProductsComponent()}
        <Clear />
      </ErrorBoundary>
    ) : (
      getRelatedProductsComponent()
    );
  });
